<template>
  <v-card dark class="gf-fluctuation mb-8" flat>
    <v-card-title class="px-6 pt-5 d-flex flex-wrap gf-fluctuation__header">
      <div>
        <div class="text-h6">Fluktuation im Vergleich: alle Mitglieder & Groupfitness</div>
        <div class="text-body-2 grey--text text--lighten-1 mt-1">Gemeinsame Basis: Personen mit Hauptvertrag am Monatsanfang.</div>
      </div>
      <v-spacer />
      <v-select
        v-if="scopeOptions.length"
        v-model="scope"
        :items="scopeOptions"
        label="Standort"
        outlined dense hide-details
        class="gf-fluctuation__scope" />
      <v-btn small outlined :disabled="!rows.length" @click="downloadCsv"><v-icon small left>mdi-download</v-icon>CSV</v-btn>
    </v-card-title>

    <v-card-text class="px-6 pb-6">
      <v-alert v-if="!currentComparison" type="info" text class="mt-3 mb-0">
        Der GF-Vergleich für mindestens vier Kursbesuche in den vorherigen zwölf Monaten liegt noch nicht vor.
        Er wird mit der nächsten erfolgreichen Berechnung erstellt.
      </v-alert>
      <template v-else>
        <p class="text-body-2 mt-2 mb-4">
          Groupfitness: mindestens {{ currentComparison.minVisits }} bestätigte Kursbesuche in den {{ currentComparison.lookbackMonths }} Monaten vor dem jeweiligen
          Auswertungsmonat. Jeder Kunde zählt pro Monat einmal; Besuche an anderen eigenen Standorten zählen mit. Derselbe Kurstermin zählt nur einmal.
        </p>
        <v-row dense>
          <v-col cols="12" sm="4" v-for="group in summaryGroups" :key="group.key">
            <div class="gf-fluctuation__metric" :class="'gf-fluctuation__metric--' + group.key">
              <div class="text-body-2">{{ group.label }}</div>
              <div class="text-h4 font-weight-bold mt-2">{{ formatRate(summary[group.key].rate) }}</div>
              <div class="text-caption grey--text text--lighten-1 mt-2">
                {{ summary.months ? formatNumber(summary[group.key].averageMembers) : '–' }} Mitglieder Ø/Monat ·
                {{ summary.months ? formatNumber(summary[group.key].cancellations) : '–' }} Kündigungen
              </div>
            </div>
          </v-col>
          <v-col cols="12" sm="4">
            <div class="gf-fluctuation__metric">
              <div class="text-body-2">GF gegenüber allen Mitgliedern</div>
              <div class="text-h4 font-weight-bold mt-2">{{ formatDifference(summary.difference) }}</div>
              <div class="text-caption grey--text text--lighten-1 mt-2">Prozentpunkte · negative Werte bedeuten eine niedrigere GF-Quote</div>
            </div>
          </v-col>
        </v-row>
        <div class="text-caption grey--text text--lighten-1 mt-3">
          Auf ein Jahr hochgerechnete Monatsquoten · {{ summary.months }} vollständig vergleichbare Monate · Datenstand {{ currentComparison.asOf }}
        </div>
        <div v-if="unlinkedAccounts" class="text-caption amber--text text--lighten-3 mt-2">
          Für den letzten angezeigten Monat: {{ unlinkedAccounts }} Konten im Unternehmen mit mindestens {{ currentComparison.minVisits }} bestätigten
          Kursbesuchen in den vorherigen {{ currentComparison.lookbackMonths }} Monaten ohne eindeutige TAC-Zuordnung.
          Ihre Besuche können der GF-Gruppe nicht zugerechnet werden; die GF-Gruppe kann dadurch unvollständig sein.
        </div>
        <div v-if="emailMatchedAccounts" class="text-caption grey--text text--lighten-1 mt-2">
          {{ emailMatchedAccounts }} Konten mit mindestens {{ currentComparison.minVisits }} Besuchen wurden für diesen Monat über eine eindeutige E-Mail-Adresse zugeordnet.
        </div>
        <div v-if="currentComparison.emailMatching === 'unavailable'" class="text-caption amber--text text--lighten-3 mt-2">
          Der zusätzliche E-Mail-Abgleich war bei dieser Berechnung nicht verfügbar. Die GF-Gruppe kann dadurch unvollständig sein.
        </div>
        <div v-else-if="!currentComparison.emailMatching" class="text-caption grey--text text--lighten-1 mt-2">
          E-Mail-Abgleich und bereinigte Zuordnungshinweise stehen nach der nächsten Statistikberechnung zur Verfügung.
        </div>
        <div v-if="!hasTotalScope && scopeOptions.length > 1" class="text-caption grey--text text--lighten-1 mt-2">
          Bei einer Auswahl mehrerer einzelner Standorte erfolgt der Vergleich je Standort. Die Gesamtsicht ist bei Auswahl aller Standorte verfügbar.
        </div>

        <apexchart v-if="displayMode === 'chart' && rows.length" class="mt-5" type="line" height="300" :options="chartOptions" :series="series" />
        <v-simple-table v-else-if="rows.length" class="transparent mt-5 gf-fluctuation__table">
          <thead>
            <tr>
              <th>Monat</th><th class="text-right">Alle: Mitglieder</th><th class="text-right">Alle: Kündigungen</th><th class="text-right">Alle: % p.a.</th>
              <th class="text-right">GF: Mitglieder</th><th class="text-right">GF: Kündigungen</th><th class="text-right">GF: % p.a.</th><th class="text-right">Differenz (PP)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.month">
              <td class="text-no-wrap">{{ row.month }}<div class="text-caption grey--text">{{ statusLabel(row) }}</div></td>
              <td class="text-right">{{ formatNumber(row.all.members) }}</td>
              <td class="text-right">{{ row.status === 'ready' ? formatNumber(row.all.cancellations) : '–' }}</td>
              <td class="text-right">{{ formatRate(row.all.rate) }}</td>
              <td class="text-right">{{ formatNumber(row.groupFitness.members) }}</td>
              <td class="text-right">{{ row.status === 'ready' ? formatNumber(row.groupFitness.cancellations) : '–' }}</td>
              <td class="text-right">{{ formatRate(row.groupFitness.rate) }}</td>
              <td class="text-right">{{ formatDifference(difference(row)) }}</td>
            </tr>
          </tbody>
        </v-simple-table>
        <div v-else class="text-body-2 grey--text mt-5">Für diese Auswahl liegen keine Vergleichsdaten vor.</div>
        <div v-if="unavailableMonths" class="text-caption grey--text text--lighten-1 mt-3">
          {{ unavailableMonths }} Monate ohne vergleichbare Quote: laufender Monat, fehlende Kündigungsdaten oder keine Mitglieder in einer der Gruppen.
          Fehlende Werte werden als Lücke dargestellt.
        </div>

        <v-expansion-panels flat class="mt-5">
          <v-expansion-panel class="gf-fluctuation__method">
            <v-expansion-panel-header>So wird der Vergleich berechnet</v-expansion-panel-header>
            <v-expansion-panel-content>
              <p>Beide Gruppen bestehen aus Personen mit aktivem Hauptvertrag am Monatsanfang. Gezählt werden die im jeweiligen Monat
                erfassten Hauptvertragskündigungen dieser Personen. Zusatzverträge und Kündigungen wegen eines Abo-Upgrades zählen nicht.</p>
              <p>Monatsquote: Kündigungen ÷ Mitglieder am Monatsanfang × 12 × 100. Die Übersicht verwendet für beide Gruppen dieselben
                vollständigen Monate und gewichtet nach Mitgliederzahl. Sie zeigt eine Hochrechnung, keine gemessene Jahres-Austrittsquote.</p>
              <p>Die GF-Gruppe ist eine Teilmenge aller Mitglieder. Die Zuordnung folgt dem Vertragsstandort und den gespeicherten
                Anwesenheiten vor dem Auswertungsmonat. Bei der Gesamtsicht wird dieselbe Person über Standorte hinweg nur einmal gezählt.</p>
              <p class="mb-0">Die bisherige Fluktuationsanzeige darunter verwendet den gespeicherten TAC-Gesamtbestand und alle
                Hauptvertragskündigungen. Dieser Vergleich verwendet für beide Gruppen den gleichen Monatsanfangsbestand;
                deshalb kann die Quote „Alle Mitglieder“ von der bisherigen Anzeige abweichen. Unterschiede zeigen einen Zusammenhang,
                belegen aber keine Ursache.</p>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from "vue-apexcharts"

export default {
  name: "HomeDashboardGroupFitnessFluctuation",
  components: { apexchart: VueApexCharts },
  props: {
    comparison: { type: Object, default: null },
    studios: { type: Array, default: () => [] },
    months: { type: Array, default: () => [] },
    displayMode: { type: String, default: "chart" },
  },
  data: () => ({ scope: "total", summaryGroups: [{ key: "all", label: "Alle Mitglieder" }, { key: "groupFitness", label: "Mit Groupfitness-Teilnahme" }] }),
  computed: {
    currentComparison() {
      const data = this.comparison
      return data && data.version === 2 && data.minVisits === 4 && data.lookbackMonths === 12 ? data : null
    },
    hasTotalScope() {
      return Boolean(this.currentComparison && this.currentComparison.total && this.currentComparison.studioCodes.every((code) => this.studios.some((studio) => studio.code === code)))
    },
    scopeOptions() {
      if (!this.currentComparison) return []
      const options = this.hasTotalScope ? [{ text: "Alle ausgewählten Standorte", value: "total" }] : []
      return options.concat(this.studios.filter((studio) => this.currentComparison.centers[studio.code]).map((studio) => ({ text: studio.name, value: studio.code })))
    },
    rows() {
      if (!this.currentComparison) return []
      const selected = this.scopeOptions.some((option) => option.value === this.scope) ? this.scope : this.scopeOptions[0] && this.scopeOptions[0].value
      const data = selected === "total" ? this.currentComparison.total : this.currentComparison.centers[selected]
      return this.months.filter((month) => data && data[month]).map((month) => ({ month, ...data[month] }))
    },
    summary() {
      const paired = this.rows.filter((row) => row.status === "ready" && row.all.members > 0 && row.groupFitness.members > 0)
      const result = { months: paired.length }
      for (const key of ["all", "groupFitness"]) {
        const members = paired.reduce((sum, row) => sum + row[key].members, 0)
        const cancellations = paired.reduce((sum, row) => sum + row[key].cancellations, 0)
        result[key] = { cancellations, averageMembers: paired.length ? members / paired.length : 0, rate: members > 0 ? cancellations * 1200 / members : null }
      }
      result.difference = result.all.rate !== null && result.groupFitness.rate !== null ? result.groupFitness.rate - result.all.rate : null
      return result
    },
    unavailableMonths() { return this.rows.length - this.summary.months },
    latestCoverage() {
      const last = this.rows[this.rows.length - 1]
      const coverage = last && this.currentComparison.coverage && this.currentComparison.coverage[last.month]
      // Old snapshots counted every account with one visit. Keep their valid
      // comparison visible, but never relabel those warning counts as >= 4.
      return coverage && coverage.minVisits === this.currentComparison.minVisits ? coverage : null
    },
    unlinkedAccounts() { return this.latestCoverage ? this.latestCoverage.unlinkedAccounts : 0 },
    emailMatchedAccounts() { return this.latestCoverage ? this.latestCoverage.emailMatchedAccounts : 0 },
    series() {
      return this.summaryGroups.map((group) => ({ name: group.label, data: this.rows.map((row) => row[group.key].rate) }))
    },
    chartOptions() {
      return {
        chart: { toolbar: { show: false }, background: "transparent", foreColor: "#cbd5e1", animations: { enabled: false } },
        colors: ["#67b8ff", "#55ca82"], stroke: { width: 3, curve: "straight" }, markers: { size: 4 },
        dataLabels: { enabled: false }, legend: { position: "top", horizontalAlign: "left" },
        xaxis: { categories: this.rows.map((row) => row.month) },
        yaxis: { min: 0, labels: { formatter: (value) => this.formatRate(value) } },
        tooltip: { theme: "dark", shared: true, intersect: false, y: { formatter: (value) => this.formatRate(value) } },
        grid: { borderColor: "rgba(255,255,255,0.08)" },
      }
    },
  },
  watch: {
    scopeOptions: { immediate: true, handler(options) { if (!options.some((option) => option.value === this.scope)) this.scope = options.length ? options[0].value : "total" } },
  },
  methods: {
    formatNumber(value) { return Number(value).toLocaleString("de-CH", { maximumFractionDigits: 0 }) },
    formatRate(value) { return value == null || !Number.isFinite(value) ? "–" : `${value.toLocaleString("de-CH", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` },
    formatDifference(value) { return value == null ? "–" : `${value > 0 ? "+" : ""}${value.toLocaleString("de-CH", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}` },
    difference(row) { return row.all.rate !== null && row.groupFitness.rate !== null ? row.groupFitness.rate - row.all.rate : null },
    statusLabel(row) {
      if (row.status === "in_progress") return "Monat noch nicht abgeschlossen"
      if (row.status !== "ready") return "Kündigungsdaten unvollständig"
      return row.groupFitness.members ? "" : "Keine zugeordneten GF-Mitglieder"
    },
    downloadCsv() {
      if (!this.currentComparison) return
      const scopeLabel = (this.scopeOptions.find((option) => option.value === this.scope) || {}).text || ""
      const csv = [["Monat", "Standort", "GF-Rückblick (Monate)", "GF-Mindestbesuche", "Status", "Alle Mitglieder", "Alle Kündigungen", "Alle Quote (% p.a.)", "GF-Mitglieder", "GF-Kündigungen", "GF-Quote (% p.a.)", "Differenz (Prozentpunkte)"]]
      for (const row of this.rows) csv.push([
        row.month, scopeLabel, this.currentComparison.lookbackMonths, this.currentComparison.minVisits, this.statusLabel(row) || "Vollständig",
        row.all.members, row.status === "ready" ? row.all.cancellations : null, row.all.rate,
        row.groupFitness.members, row.status === "ready" ? row.groupFitness.cancellations : null, row.groupFitness.rate, this.difference(row),
      ])
      const escape = (value) => {
        let text = value == null ? "" : String(value)
        if (typeof value === "string" && /^[=+\-@\t\r]/.test(text)) text = "'" + text
        return '"' + text.replace(/"/g, '""') + '"'
      }
      const url = URL.createObjectURL(new Blob(["\uFEFF" + csv.map((row) => row.map(escape).join(";")).join("\r\n")], { type: "text/csv;charset=utf-8;" }))
      const link = document.createElement("a")
      link.href = url
      link.download = "fluktuation-groupfitness.csv"
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 0)
    },
  },
}
</script>

<style scoped>
.gf-fluctuation { background: #202731 !important; border: 1px solid rgba(85, 202, 130, 0.24); border-radius: 18px; }
.gf-fluctuation__header { gap: 12px; }
.gf-fluctuation__scope { flex: 0 1 280px; min-width: 220px; }
.gf-fluctuation__metric { height: 100%; padding: 18px; border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 12px; }
.gf-fluctuation__metric--all { border-top: 3px solid #67b8ff; }
.gf-fluctuation__metric--groupFitness { border-top: 3px solid #55ca82; }
.gf-fluctuation__table td, .gf-fluctuation__table th { white-space: nowrap; }
.gf-fluctuation__method { background: rgba(255, 255, 255, 0.04) !important; }
@media (max-width: 600px) { .gf-fluctuation__scope { flex-basis: 100%; } }
</style>
